import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, Link } from "react-router-dom";
import type { VaultEntry, Prompt, Ritual, Project, EntrySummary } from "@akhshy/types";
import { client } from "../../lib/api.ts";
import { useAsync } from "../../lib/useAsync.ts";
import { renderMarkdown } from "../../lib/markdown.tsx";
import { CopyButton } from "../atoms/CopyButton.tsx";
import { LaunchInLlm } from "../atoms/LaunchInLlm.tsx";
import { Spinner } from "../atoms/Spinner.tsx";
import { Tape } from "../atoms/Tape.tsx";

type EntryType = "vault" | "prompt" | "ritual" | "project";
type AnyEntry = VaultEntry | Prompt | Ritual | Project;

const LABEL: Record<EntryType, string> = {
  vault: "from the vault",
  prompt: "prompt",
  ritual: "ritual",
  project: "project",
};

function resource(type: EntryType) {
  return type === "vault"
    ? client.vault
    : type === "prompt"
      ? client.prompts
      : type === "ritual"
        ? client.rituals
        : client.projects;
}

function titleOf(type: EntryType, e: AnyEntry): string {
  if (type === "project") return (e as Project).name;
  return (e as VaultEntry | Prompt | Ritual).title;
}

function bodyOf(type: EntryType, e: AnyEntry): string {
  switch (type) {
    case "vault": {
      const v = e as VaultEntry;
      return v.content ?? v.description;
    }
    case "prompt":
      return (e as Prompt).body;
    case "ritual":
      return (e as Ritual).content;
    case "project":
      return (e as Project).description;
  }
}

/** Where a related entry of any type links to. */
function relatedPath(item: EntrySummary): string {
  switch (item.entryType) {
    case "vault":
      return `/vault/${item.slug ?? item.entryId}`;
    case "prompt":
      return `/prompts/${item.slug ?? item.entryId}`;
    case "ritual":
      return "/ritual";
    case "project":
      return "/projects";
  }
}

/** Resolve by slug; fall back to id for legacy links. */
async function load(type: EntryType, handle: string) {
  const api = resource(type);
  try {
    const entry = (await api.getBySlug(handle)) as AnyEntry;
    // Cross-type "also tagged" rail; garnish, so failures collapse to empty.
    const related = await client
      .relatedAcross(type, entry.slug ?? handle)
      .then((r) => r.items)
      .catch(() => [] as EntrySummary[]);
    return { entry, related };
  } catch {
    const entry = (await api.get(handle)) as AnyEntry;
    return { entry, related: [] as EntrySummary[] };
  }
}

/**
 * A popup that shows a single prompt / vault note (etc.) resolved from the
 * `:slug` URL segment. Rendered as a child route over the list page, so the
 * URL is shareable and survives a refresh, while the list stays underneath.
 */
export function EntryModal({
  type,
  listPath,
}: {
  type: EntryType;
  listPath: string;
}) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const state = useAsync(() => load(type, slug ?? ""), [type, slug]);

  const close = () => navigate(listPath);

  // Esc to close + lock background scroll while open.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listPath]);

  // Count a vault note as "opened" when it resolves.
  useEffect(() => {
    if (type === "vault" && state.data?.entry.id) {
      void client.metrics.recordVaultOpened(state.data.entry.id).catch(() => {});
    }
  }, [type, state.data?.entry.id]);

  const entry = state.data?.entry ?? null;
  const related = state.data?.related ?? [];
  const isPrompt = type === "prompt";

  // Portal to <body> so the fixed overlay escapes any transformed ancestor
  // (e.g. the section's slide-in animation), covering the full viewport.
  return createPortal(
    <div
      className="entry-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={entry ? titleOf(type, entry) : LABEL[type]}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="entry-modal">
        <button type="button" className="entry-modal-close" onClick={close} aria-label="Close">
          ✕
        </button>
        <Tape />

        {state.loading ? (
          <Spinner />
        ) : state.error || !entry ? (
          <div className="entry-modal-missing">
            <p>That entry doesn't exist (or isn't published yet).</p>
            <button type="button" className="expand-btn" onClick={close}>
              ← back to the list
            </button>
          </div>
        ) : (
          <>
            <p className="entry-modal-kicker">{LABEL[type]}</p>
            <h3 className="entry-modal-title">{titleOf(type, entry)}</h3>

            {entry.tags.length > 0 && (
              <div className="entry-tags">
                {entry.tags.map((t) => (
                  <span key={t} className="entry-tag">
                    #{t}
                  </span>
                ))}
              </div>
            )}

            {isPrompt ? (
              <>
                <pre className="entry-modal-pre">{bodyOf(type, entry)}</pre>
                <div className="entry-modal-actions">
                  <CopyButton
                    text={bodyOf(type, entry)}
                    onCopied={() =>
                      void client.metrics.recordPromptCopied(entry.id).catch(() => {})
                    }
                  />
                  <LaunchInLlm promptId={entry.id} body={bodyOf(type, entry)} />
                </div>
              </>
            ) : (
              <div className="entry-modal-body">{renderMarkdown(bodyOf(type, entry))}</div>
            )}

            {related.length > 0 && (
              <aside className="related-rail" aria-label="Related">
                <h4 className="related-head">Related — shares a tag with this one</h4>
                <ul className="related-list">
                  {related.map((r) => (
                    <li key={`${r.entryType}:${r.entryId}`}>
                      <span className={`entry-badge entry-badge--${r.entryType}`}>
                        {r.entryType}
                      </span>{" "}
                      <Link className="related-link" to={relatedPath(r)}>
                        {r.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </aside>
            )}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
